/**
 * Sticky image + content — mobile card scroller pager (dots + progress line)
 * + hash deep-links to cards (sticky header offset via CSS scroll-margin)
 */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 989px)';
  var initialized = new WeakSet();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollCardIntoView(card, options) {
    if (!card) return;

    var opts = options || {};
    var reduced = prefersReducedMotion();
    var isLast =
      opts.isLast === true ||
      (card.parentElement && card === card.parentElement.lastElementChild);
    var behavior = opts.instant || reduced ? 'auto' : 'smooth';

    card.scrollIntoView({
      behavior: behavior,
      block: opts.block || 'start',
      inline: isLast ? 'end' : 'start',
    });
  }

  function scrollToHashTarget(options) {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    var id = decodeURIComponent(hash.slice(1));
    if (!id) return;

    var card = document.getElementById(id);
    if (!card || !card.classList.contains('sticky-image-richtext__card')) return;

    // Wait a frame so --header-sticky-offset from header.js is applied.
    window.requestAnimationFrame(function () {
      scrollCardIntoView(card, {
        instant: options && options.instant,
        block: 'start',
      });
    });
  }

  function init(root) {
    if (!root || initialized.has(root)) return;

    var scroller = root.querySelector('.sticky-image-richtext__cards');
    var pager = root.querySelector('[data-sticky-image-dots]');
    if (!scroller || !pager) return;

    var dots = Array.prototype.slice.call(
      pager.querySelectorAll('[data-sticky-image-dot]')
    );
    var cards = Array.prototype.slice.call(
      scroller.querySelectorAll('.sticky-image-richtext__card')
    );
    var progressFill = pager.querySelector('[data-sticky-image-progress-fill]');
    if (dots.length < 2 || cards.length < 2) return;

    initialized.add(root);

    var ticking = false;
    var mobileQuery = window.matchMedia(MOBILE_MQ);

    function setActive(index) {
      dots.forEach(function (dot, i) {
        var active = i === index;
        var passed = i < index;
        dot.classList.toggle('is-active', active);
        dot.classList.toggle('is-passed', passed);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function setProgress(progress) {
      if (!progressFill) return;
      var value = Math.min(Math.max(progress, 0), 1);
      progressFill.style.transform = 'scaleX(' + value.toFixed(4) + ')';
    }

    function updateFromScroll() {
      if (!mobileQuery.matches) {
        setProgress(0);
        return;
      }

      var max = scroller.scrollWidth - scroller.clientWidth;
      var progress = max > 1 ? scroller.scrollLeft / max : 0;
      setProgress(progress);

      var viewCenter = scroller.scrollLeft + scroller.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;

      cards.forEach(function (card, i) {
        var center = card.offsetLeft + card.offsetWidth / 2;
        var dist = Math.abs(center - viewCenter);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });

      setActive(best);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        updateFromScroll();
      });
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        var card = cards[index];
        if (!card) return;

        scrollCardIntoView(card, {
          isLast: index === cards.length - 1,
          block: 'nearest',
        });
        setActive(index);
      });
    });

    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateFromScroll);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(updateFromScroll);
    }

    updateFromScroll();
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('.sticky-image-richtext');
    Array.prototype.forEach.call(roots, init);
  }

  function onReady() {
    initAll();
    // Instant jump on first paint so the sticky header offset is correct
    // before the user notices; hashchange uses smooth when available.
    scrollToHashTarget({ instant: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  window.addEventListener('hashchange', function () {
    scrollToHashTarget({ instant: false });
  });

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target) return;

    if (target.classList && target.classList.contains('sticky-image-richtext')) {
      initialized.delete(target);
      init(target);
      return;
    }

    var root = target.querySelector && target.querySelector('.sticky-image-richtext');
    if (root) {
      initialized.delete(root);
      init(root);
    }
  });
})();
